import { int3DigitFormat, isNumberBetweenEquals } from "../number/number";
import { getQueryByName } from "../url/url";
import {
  CollectionAllKey,
  Collections,
  DateRangeLifetime,
  dateRangeSearchAll, facetsKeysAI, facetsKeysBook, facetsKeysOmsk, facetsKeysPhrase, facetsKeysSuggestion,
  isRestricted,
  mapSearchTypeToNamesExtras,
  Order,
  Period,
  Periods,
  PeriodWithCustom,
  QUERY_TYPE,
  queryPhraseRegExp,
  SEARCH_TOTAL_MAX,
  SearchListType,
  SearchType,
  SearchTypes,
  yearCompilations
} from "./search.constants";
import { getLanguageDefault } from "../user-agent";
import { sortByArray } from "../array";

const isYearRangeLifetime = (startYear, endYear) => {
  return isNumberBetweenEquals(startYear, DateRangeLifetime.start, DateRangeLifetime.end)
    || isNumberBetweenEquals(endYear, DateRangeLifetime.start, DateRangeLifetime.end);
};

const isYearRangeCompilations = (startYear, endYear = startYear) => {
  return startYear >= yearCompilations || endYear >= yearCompilations;
};

export const getDateRangeFilterBook = (books, dateRange) => {
  if (dateRange !== dateRangeSearchAll) {
    let checkFn;

    if (dateRange === Period.lifetime) {
      checkFn = isYearRangeLifetime;
    } else if (dateRange === Period.compilations) {
      checkFn = isYearRangeCompilations;
    }

    if (checkFn) {
      return books.filter((book) => {
        if (book.startYear || book.endYear) {
          return checkFn(book.startYear, book.endYear);
        }

        return checkFn(book.year);
      });
    }
  }
  return books;
};

export const isSettingPeriodValid = (v) => Periods.includes(v);

export const isCollectionValid = (collection) => {
  return Collections.includes(collection) || CollectionAllKey === collection;
};

export const getTopics = (topics) => {
  return Object.keys(topics).map((key) => {
    return { topic: key, count: topics[key] };
  });
};

export const getSearchTypeByPeriod = (period, defaultType) => {
  if (period === Period.dictionary) {
    return SearchType.dictionary;
  }

  return defaultType || SearchType.basic;
};

export const getExtrasBySearchType = (type, paramsOrFnGetParam) => {
  const fields =
    (type && SearchTypes.includes(type))
      ? mapSearchTypeToNamesExtras[type]
      : mapSearchTypeToNamesExtras[SearchType.basic];
  const result = {};
  for (const name of fields) {
    const value = typeof paramsOrFnGetParam === "function"
      ? paramsOrFnGetParam(name)
      : paramsOrFnGetParam[name];
    if (value !== undefined) {
      result[name] = value;
    }
  }
  return result;
};

export const getExtrasByUrl = (paramsUrl) => {
  return getExtrasBySearchType(
    getQueryByName("type", paramsUrl),
    (name) => getQueryByName(name, paramsUrl),
  );
};

export const isBunchLoaded = (items, total, offset, limit) => {
  const indexItemStart = items.findIndex((item) => item.index === offset);
  if (indexItemStart === -1) {
    return false;
  }

  const indexItemLast = Math.min(indexItemStart + limit, total - 1);
  const itemLast = items[items.length - 1];
  if (itemLast && itemLast.index < indexItemLast) {
    return false;
  }

  const chunk = items.slice(indexItemStart, indexItemLast + 1);

  return (chunk.length - 1) === limit && chunk.every((item, i) => item.index === offset + i);
};

export const fixSearchPreferExact = async (fetchFn, offset, limit) => {
  const check1 = await fetchFn(QUERY_TYPE.EXACT, 0, 1);

  let total = check1.total;

  const results = [];
  if (total > offset) {
    const response1 = await fetchFn(QUERY_TYPE.EXACT, offset, limit);
    results.push(...response1.results);
  }

  const req2Offset = offset + results.length - total;
  const req2Limit = limit - results.length;
  if (req2Limit > 0 && req2Offset < SEARCH_TOTAL_MAX && req2Offset > -1) {
    const response2 = await fetchFn(QUERY_TYPE.EXCLUDE_EXACT, req2Offset, req2Limit);

    const response2ResultsIndexUpdated = response2.results.map((item) => {
      return {
        ...item,
        index: item.index + total,
      };
    });

    total += response2.total;
    results.push(...response2ResultsIndexUpdated);
  } else {
    const check2 = await fetchFn(QUERY_TYPE.EXCLUDE_EXACT, 0, 1);
    total += check2.total;
  }

  return { total, results, };
};

export const getSearchShortcut = (
  shortcuts,
  currentSearchLang, newLang, defLang,
  isLogin, libraryLanguages, query
) => {
  let shortCutData;
  let languageUpdated;
  if (shortcuts && shortcuts.length > 0) {
    let langShortcut;

    if (newLang) {
      langShortcut =
        shortcuts.find((item) => newLang === item.lang && item.hits)
        || shortcuts.find((item) => newLang === item.lang);
      if (langShortcut) {
        languageUpdated = langShortcut.lang;
      }
    }

    if (!langShortcut) {
      langShortcut = shortcuts.find((item) => defLang === item.lang);
      if (langShortcut) {
        languageUpdated = defLang;
      } else {
        langShortcut = shortcuts.find((item) => item.lang === currentSearchLang);
      }
    }

    // get any lang from ML of default lang
    if (!langShortcut && isLogin && libraryLanguages.length) {
      langShortcut = shortcuts.find((item) => {
        return libraryLanguages.includes(item.lang);
      });
    }

    // get any shortcut with empty cutQuery for open a book by possible bookcode
    if (!langShortcut) {
      langShortcut = shortcuts.find((item) => !item.cutQuery);
    }

    if (langShortcut && query && query.includes("JTL") && langShortcut.book_code !== query) { // EGWW-4260
      langShortcut = shortcuts.find((item) => item.book_code === query);
    }

    shortCutData = langShortcut;
  }

  return { shortCutData, languageUpdated };
};

export const setShortcutDataParamsToSearchParams = (
  searchParams, shortcutData, baseBible, isSetLanguage = true, isPeriodCustomForSearchInPara = true
) => {
  searchParams.query = shortcutData.cutQuery;
  searchParams.queryFull = shortcutData.query;

  if (isSetLanguage) {
    searchParams.langs = [shortcutData.lang];
  }

  if (shortcutData.paraId) {
    searchParams.chapters = [shortcutData.paraId];
  } else {
    searchParams.books = [shortcutData.bookId];
  }

  if (!searchParams.extras) {
    searchParams.extras = {};
  }

  if (shortcutData.bookId === baseBible) {
    searchParams.extras.period = Period.myBible;
  } else {
    // For next app "all" so far.
    searchParams.extras.period = isPeriodCustomForSearchInPara
      ? PeriodWithCustom.custom
      : Period.all;
  }

  return searchParams;
};

export const getFolderType = (list, fieldCheck = "className") => {
  let realType = "";

  list.forEach((item) => {
    const itemType = item[fieldCheck];

    if (itemType && Collections.includes(itemType)) {
      realType = itemType;
    }
  });

  return realType;
};

export const isQueryPhrase = (query) => queryPhraseRegExp.test(query.trim());

export const isQueryQuestion = (query) => query.trim().indexOf("?") === query.trim().length - 1;

// Ignore a query that starts with book code "HOW"
export const isQueryOnlyText = (query) => (
  query
  && (
    (query.toLowerCase().indexOf("how") === 0 && query.length !== 3)
    || query.includes("*")
    || isQueryQuestion(query)
  )
);

export const handleSearchShortcut = (shortcut, {
  onShortcutBook,
  onSearchInBookOrChapter,
  onShortcutPara,
  onTextSearch,
}) => {
  if (
    shortcut
    && shortcut.bookId
    && !(shortcut.shortCutType === "search" && shortcut.hits === 0 && shortcut.paraId)
  ) {
    const { shortCutType, paraId, hits } = shortcut;

    if (shortCutType === "search") {
      if (hits === 0) { // Opens a book
        onShortcutBook();
      } else { // Opens search results page for search word in book e.g. try "aa hope"
        onSearchInBookOrChapter();
      }
    } else if (paraId) { // Opens a para of a book
      onShortcutPara();
    }
  } else { // Simple text search
    if (onTextSearch) {
      onTextSearch();
    }
  }
};

export const isOrderOptionDisabledByPeriod = (itemId, period) => {
  if (itemId === Order.wrel && (period === PeriodWithCustom.myBible || period === PeriodWithCustom.apl)) {
    return true;
  }

  return (
    itemId === Order.rel &&
    (period === PeriodWithCustom.compilations
      || period === PeriodWithCustom.lifetime || period === PeriodWithCustom.apl)
  );
};

export const updateSearchPeriod = (period) => {
  const params = { period };

  if (period === PeriodWithCustom.lifetime || period === PeriodWithCustom.compilations) {
    params.order = Order.wrel;
  }

  if (period === PeriodWithCustom.myBible || period === PeriodWithCustom.apl) {
    params.order = Order.seq;
  }

  return params;
};

export function _makeLangSort(
  mainTreeLangs, libraryLanguages, languageUI,
  langList, langScoreMap,
) {
  // Firstly, sort by: {score | lang books count}
  const mapLangCodeToBookCount = {};
  mainTreeLangs.forEach((item) => {
    mapLangCodeToBookCount[item.code] = item.book_count;
  });

  let sortedList = [...langList];
  sortedList.sort((a, b) => {
    if (langScoreMap && langScoreMap[a] && langScoreMap[b]) {
      if (langScoreMap[a] > langScoreMap[b]) {
        return -1;
      }
      if (langScoreMap[a] < langScoreMap[b]) {
        return 1;
      }
    }
    if (mapLangCodeToBookCount[a] && mapLangCodeToBookCount[b]) {
      if (mapLangCodeToBookCount[a] > mapLangCodeToBookCount[b]) {
        return -1;
      }
      if (mapLangCodeToBookCount[a] < mapLangCodeToBookCount[b]) {
        return 1;
      }
    }

    return 0;
  });
  // =================

  // Then, sort by (order with priority):
  // {ML default lang}, {languageUI}, {ML rest langs}
  const libLangsFinal = (libraryLanguages && libraryLanguages.length)
    ? [...libraryLanguages]
    : [getLanguageDefault()];

  let libLangsSorted = libLangsFinal;
  // Sort non default languages of ML, base on score and UI language
  const libNonDefaultLangs = libLangsFinal.slice(1);
  if (libNonDefaultLangs.length > 1) {
    libNonDefaultLangs.sort((a, b) => {
      if (langScoreMap) {
        if (langScoreMap[a] > langScoreMap[b]) {
          return -1;
        }
        if (langScoreMap[a] < langScoreMap[b]) {
          return 1;
        }
      }

      if (a === languageUI) {
        return -1;
      }
      if (b === languageUI) {
        return 1;
      }
      return 0;
    });

    libLangsSorted = [libLangsFinal[0], ...libNonDefaultLangs];
  }
  // ===============================================================

  sortedList = sortByArray(sortedList, libLangsSorted);
  // =============================

  return sortedList;
}

export const getResultsCount = (count, max = SEARCH_TOTAL_MAX, separator = ",") => {
  if (max && isRestricted(count, max)) {
    return `${int3DigitFormat(max, separator)}+`;
  }
  return int3DigitFormat(count, separator);
};

const QueryRegex = /\w*/u;

export const validateSearchQuery = (query) => {
  if (!query) {
    return false;
  }
  return QueryRegex.test(query);
};

export const getSearchListType = (section) => {
  if (section) {
    if (facetsKeysOmsk.includes(section)) {
      return SearchListType.OMSK;
    } else if (facetsKeysPhrase.includes(section)) {
      return SearchListType.PHRASE;
    } else if (facetsKeysBook.includes(section)) {
      return SearchListType.BOOK;
    } else if (facetsKeysSuggestion.includes(section)) {
      return SearchListType.SUGGESTION;
    } else if (facetsKeysAI.includes(section)) {
      return SearchListType.AI;
    }
  }

  return SearchListType.DEFAULT;
};
